import { useState } from "react";
import { useT } from "@/lib/translation";
import type { BrokerGuide } from "@/data/brokerGuides";

function StepImage({
  src,
  alt,
  wide,
}: {
  src: string;
  alt: string;
  wide?: boolean;
}) {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
      className={`mt-3 mx-auto block rounded-xl border border-border object-contain ${
        wide ? "w-full" : "max-w-[240px]"
      }`}
    />
  );
}

export default function BrokerGuideContent({ guide }: { guide: BrokerGuide }) {
  const t = useT();
  return (
    <div className="space-y-5 pt-1">
      <p className="text-sm text-muted-foreground">{guide.intro}</p>

      <ol className="space-y-5">
        {guide.steps.map((step, i) => (
          <li key={i} className="flex gap-4">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-semibold">
              {i + 1}
            </div>
            <div className="flex-1 pt-0.5 min-w-0">
              <p className="font-medium text-sm leading-snug">{step.title}</p>
              {/* whitespace-pre-line so blank lines in a description render as paragraph
                  breaks. Guides that embed a pasteable block rely on it. */}
              <p className="text-sm text-muted-foreground mt-0.5 whitespace-pre-line">
                {step.description}
              </p>
              {step.image && (
                <StepImage
                  src={step.image}
                  alt={t("onboarding:brokerGuide.stepAlt", {
                    step: i + 1,
                  })}
                  wide={guide.wideImages}
                />
              )}
            </div>
          </li>
        ))}
      </ol>

      {guide.note && (
        <div className="rounded-lg bg-muted px-4 py-3 text-sm text-muted-foreground whitespace-pre-line">
          <strong className="text-foreground">
            {t("onboarding:brokerGuide.note")}{" "}
          </strong>
          {guide.note}
        </div>
      )}
    </div>
  );
}
